import { Button, PlusIcon } from '../Button/Button';
import { sparkAssetUrl } from '../../utils/sparkAssetUrl';
import './EmptyState.css';

export type EmptyStateDirection = 'vertical' | 'horizontal';
export type EmptyStateSize = 'medium' | 'small';

/** Illustration + message shown when a list or section has no content. Optionally includes a CTA. */
export type EmptyStateProps = {
  title?: string;
  subtitle?: string;
  direction?: EmptyStateDirection;
  size?: EmptyStateSize;
  showSubtitle?: boolean;
  showMainButton?: boolean;
  /** Figma "Button Main" icon toggle — instances may hide the CTA's plus icon. */
  showCtaIcon?: boolean;
  ctaLabel?: string;
  onCtaClick?: () => void;
  /** Disable the CTA while a `ReadOnlyProvider` is read-only, with its reason as a tooltip. */
  ctaDisableWhenReadOnly?: boolean;
  className?: string;
};

const ILLUSTRATION_SRC: Record<EmptyStateSize, string> = {
  medium: sparkAssetUrl('illustrations/illustration-box-open.svg'),
  small: sparkAssetUrl('illustrations/illustration-box-open.svg'),
};

function EmptyStateIllustration({ size }: { size: EmptyStateSize }) {
  return (
    <div className={`spark-empty-state-illustration spark-empty-state-illustration-${size}`} aria-hidden="true">
      <img src={ILLUSTRATION_SRC[size]} alt="" />
    </div>
  );
}

export function EmptyState({
  title = 'Title',
  subtitle = 'Subtitle',
  direction = 'vertical',
  size = 'medium',
  showSubtitle = true,
  showMainButton = false,
  showCtaIcon = true,
  ctaLabel = 'CTA',
  onCtaClick,
  ctaDisableWhenReadOnly = false,
  className = '',
}: EmptyStateProps) {
  const rootClasses = [
    'spark-empty-state',
    `spark-empty-state-${direction}`,
    `spark-empty-state-${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses}>
      <EmptyStateIllustration size={size} />
      <div className="spark-empty-state-content">
        <p className="spark-empty-state-title">{title}</p>
        {showSubtitle ? <p className="spark-empty-state-subtitle">{subtitle}</p> : null}
        {showMainButton ? (
          <Button
            className="spark-empty-state-cta"
            size="small"
            variant="ghost"
            startIcon={showCtaIcon ? <PlusIcon size="small" /> : undefined}
            disableWhenReadOnly={ctaDisableWhenReadOnly}
            onClick={onCtaClick}
          >
            {ctaLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
